import { useState, type JSX } from "react";
import { useTranslation } from "react-i18next";

import { toast } from "@/shared/lib/toast";
import { Badge } from "@/shared/ui/badge";

import { useDeliveries } from "../api/use-deliveries";
import { useRetryDelivery } from "../api/use-delivery-mutations";
import { DELIVERY_STATUS_INTENT, type DeliveryStatus } from "../lib/delivery";
import { downloadDeliveriesCsv } from "../lib/export-deliveries-csv";
import { formatRelativeTime } from "../lib/notif";

const FILTER_OPTIONS: Array<{ key: "all" | DeliveryStatus; status: DeliveryStatus | undefined }> = [
  { key: "all", status: undefined },
  { key: "sent", status: "sent" },
  { key: "failed", status: "failed" },
  { key: "retrying", status: "retrying" },
];

const PAGE_SIZE = 50;

export function DeliveryLogPanel(): JSX.Element {
  const { t } = useTranslation("notifications");
  const [statusFilter, setStatusFilter] = useState<DeliveryStatus | undefined>(undefined);
  const [retryingIds, setRetryingIds] = useState<Set<string>>(new Set());
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  const handleStatusFilterChange = (status: DeliveryStatus | undefined) => {
    setStatusFilter(status);
    // Any filter change re-starts pagination at the first page.
    setVisibleCount(PAGE_SIZE);
  };

  const deliveryFilter = {
    offset: 0,
    limit: visibleCount,
    ...(statusFilter !== undefined ? { status: statusFilter } : {}),
  };
  const { data, isLoading, isError, isFetching } = useDeliveries(deliveryFilter);
  const retryDelivery = useRetryDelivery();

  if (isLoading) {
    return (
      <div data-testid="delivery-panel">
        <p className="py-8 text-center text-sm text-[var(--text-subtle)]">{t("state.loading")}</p>
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div data-testid="delivery-panel">
        <p className="py-8 text-center text-sm text-[var(--intent-danger-fg)]">
          {t("delivery.error" as never)}
        </p>
      </div>
    );
  }

  const { items, counts, total } = data;
  const hasMore = (total ?? 0) > items.length && visibleCount < 200;

  function handleRetry(id: string): void {
    setRetryingIds((prev) => new Set([...prev, id]));
    retryDelivery.mutate(id, {
      onSuccess: () => {
        toast.success(t("delivery.retry_ok" as never));
      },
      onSettled: () => {
        setRetryingIds((prev) => {
          const next = new Set(prev);
          next.delete(id);
          return next;
        });
      },
    });
  }

  function handleExport(): void {
    downloadDeliveriesCsv(items);
    toast.success(t("delivery.exported" as never));
  }

  return (
    <div data-testid="delivery-panel" className="space-y-4">
      {/* KPI strip */}
      <div className="grid grid-cols-3 gap-3">
        <div
          data-testid="delivery-kpi-sent"
          className="rounded-xl border border-[var(--premium-border)] bg-[var(--surface-raised)] p-4 text-center"
        >
          <p className="text-2xl font-bold text-[var(--text-primary)]">{counts.sent}</p>
          <p className="mt-1 text-xs text-[var(--text-secondary)]">
            {t("delivery.kpi_sent" as never)}
          </p>
        </div>
        <div
          data-testid="delivery-kpi-failed"
          className="rounded-xl border border-[var(--premium-border)] bg-[var(--surface-raised)] p-4 text-center"
        >
          <p className="text-2xl font-bold text-[var(--intent-danger-fg)]">{counts.failed}</p>
          <p className="mt-1 text-xs text-[var(--text-secondary)]">
            {t("delivery.kpi_failed" as never)}
          </p>
        </div>
        <div
          data-testid="delivery-kpi-retrying"
          className="rounded-xl border border-[var(--premium-border)] bg-[var(--surface-raised)] p-4 text-center"
        >
          <p className="text-2xl font-bold text-[var(--intent-warning-fg)]">{counts.retrying}</p>
          <p className="mt-1 text-xs text-[var(--text-secondary)]">
            {t("delivery.kpi_retrying" as never)}
          </p>
        </div>
      </div>

      {/* Filter chips + Export */}
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          {FILTER_OPTIONS.map(({ key, status }) => {
            const isActive = statusFilter === status;
            return (
              <button
                key={key}
                type="button"
                data-testid={`delivery-filter-${key}`}
                onClick={() => handleStatusFilterChange(status)}
                aria-pressed={isActive}
                className={[
                  "rounded-full px-3 py-1 text-xs font-semibold transition",
                  isActive
                    ? "bg-[var(--brand-600)] text-white"
                    : "border border-[var(--premium-border)] text-[var(--text-secondary)] hover:bg-[var(--surface-sunken)]",
                ].join(" ")}
              >
                {t(`delivery.filter_${key}` as never)}
              </button>
            );
          })}
        </div>
        <button
          type="button"
          data-testid="delivery-export"
          onClick={handleExport}
          disabled={items.length === 0}
          className="rounded-lg border border-[var(--border-default)] px-3 py-1.5 text-xs font-semibold text-[var(--text-primary)] transition hover:bg-[var(--surface-sunken)] disabled:cursor-not-allowed disabled:opacity-50"
        >
          {t("delivery.export" as never)}
        </button>
      </div>

      {/* Table */}
      <div className="overflow-x-auto rounded-xl border border-[var(--premium-border)]">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[var(--premium-border)] bg-[var(--surface-sunken)]">
              <th className="px-4 py-3 text-left text-xs font-semibold text-[var(--text-secondary)]">
                {t("delivery.th_event" as never)}
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-[var(--text-secondary)]">
                {t("delivery.th_channel" as never)}
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-[var(--text-secondary)]">
                {t("delivery.th_recipient" as never)}
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-[var(--text-secondary)]">
                {t("delivery.th_time" as never)}
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-[var(--text-secondary)]">
                {t("delivery.th_status" as never)}
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-[var(--text-secondary)]">
                {t("delivery.th_action" as never)}
              </th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-sm text-[var(--text-subtle)]">
                  {t("delivery.empty" as never)}
                </td>
              </tr>
            ) : (
              items.map((d) => {
                const intent = DELIVERY_STATUS_INTENT[d.status];
                const isPendingRetry = retryingIds.has(d.id);
                return (
                  <tr
                    key={d.id}
                    data-testid={`delivery-row-${d.id}`}
                    className="border-b border-[var(--premium-border)] last:border-0 hover:bg-[var(--surface-sunken)]"
                  >
                    <td className="px-4 py-3 font-medium text-[var(--text-primary)]">
                      {d.event_title}
                    </td>
                    <td className="px-4 py-3 text-[var(--text-secondary)]">
                      {t(`channel.${d.channel}` as never)}
                    </td>
                    <td className="px-4 py-3 font-mono text-xs text-[var(--text-secondary)]">
                      {d.recipient}
                    </td>
                    <td className="px-4 py-3 text-xs text-[var(--text-subtle)]">
                      {formatRelativeTime(d.created_at)}
                    </td>
                    <td className="px-4 py-3">
                      <Badge intent={intent} data-testid={`delivery-status-${d.id}`}>
                        {t(`delivery.status_${d.status}` as never)}
                      </Badge>
                    </td>
                    <td className="px-4 py-3">
                      {d.status !== "sent" && (
                        <button
                          type="button"
                          data-testid={`delivery-retry-${d.id}`}
                          onClick={() => handleRetry(d.id)}
                          disabled={isPendingRetry}
                          aria-label={t("delivery.retry_for" as never, { title: d.event_title })}
                          className="rounded-lg border border-[var(--brand-500)] px-2.5 py-1 text-xs font-semibold text-[var(--brand-600)] transition hover:bg-[var(--brand-50)] disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          {t("delivery.retry" as never)}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {hasMore && (
        <div className="flex justify-center">
          <button
            type="button"
            data-testid="delivery-load-more"
            onClick={() => setVisibleCount((c) => c + PAGE_SIZE)}
            disabled={isFetching}
            className="rounded-lg border border-[var(--border-default)] px-3 py-1.5 text-xs font-semibold text-[var(--text-primary)] transition hover:bg-[var(--surface-sunken)] disabled:cursor-not-allowed disabled:opacity-50"
          >
            {t("action.load_more" as never)}
          </button>
        </div>
      )}
    </div>
  );
}
